import { Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { GenFileUpload } from '@pinnacleautomotive/clarity-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { amplifyApi } from '../hooks/amplifyApi';
import CurrrencyFormField from './CurrencyFormField';

export const nullish = val => typeof val === 'undefined' || val === null;

export default function EditServicePopup({
  service,
  onClose,
  onSave,
  isLoading,
}) {
  const { t } = useTranslation('orders');
  const [quantity, setQuantity] = useState(0);
  const [comments, setComments] = useState('');
  const [price, setPrice] = useState(0);
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const amount = price * quantity;

  useEffect(() => {
    if (!service) return;
    setQuantity(+service.quantity || 1);
    setPrice(service.price || 0);
    setComments(service.comments || '');
    setFiles(service.files || []);
  }, [service, setQuantity, setPrice]);

  return (
    <Dialog open={!!service} maxWidth="xs" fullWidth onClose={onClose}>
      <DialogTitle
        sx={{ background: theme => theme.palette.grey[50] }}
        component={Stack}
        direction="row"
        alignItems="center"
      >
        <Typography variant="subtitle2" flexGrow={1}>
          {service?.service?.name}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 6,
            color: theme => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Stack gap={2} pt={3}>
          <CurrrencyFormField
            value={price}
            onChange={e => setPrice(+e.target.value)}
            label={t('individualPrice')}
            error={nullish(price)}
            variant="outlined"
            size="small"
          />
          <Stack direction="row" gap={1.5}>
            <TextField
              fullWidth
              label={t('serviceOrderquantity')}
              size="small"
              type="number"
              onFocus={event => event.target.select()}
              inputProps={{ min: 1, 'data-testid': 'quantity' }}
              error={!quantity}
              autoComplete="off"
              value={quantity}
              onChange={e => setQuantity(+e.target.value)}
            />
            <CurrrencyFormField
              value={amount}
              label={t('totalPrice')}
              variant="outlined"
              size="small"
              fullWidth
              disabled
            />
          </Stack>
          <Stack>
            <Typography variant="overline">{t('comments')}</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder={t('newOrder.extras.notesPlaceholder')}
              variant="outlined"
              error={comments.length > 500}
              size="small"
              value={comments}
              onChange={e => setComments(e.target.value)}
            />
          </Stack>
          <Stack>
            <Typography variant="overline">{t('photos')}</Typography>
            <GenFileUpload
              variant="dense"
              key={service?.servicePriceId}
              accept={['image/*']}
              onChange={setFiles}
              currentFiles={service?.files}
              multiple={true}
              amplifyApi={amplifyApi}
              uploadUrl="/uploads/reconditions/estimates/photos"
              onChangeLoading={isUploadingFiles =>
                setIsUploading(isUploadingFiles)
              }
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isLoading || isUploading}
            sx={{
              color: 'black',
              borderColor: 'lightgray',
              '&:hover': {
                backgroundColor: 'lightgray',
                borderColor: 'lightgray',
              },
            }}
          >
            {t('global:genericButtons.cancel')}
          </Button>
          <LoadingButton
            color="primary"
            variant="contained"
            loading={isLoading || isUploading}
            disabled={nullish(price) || nullish(quantity) || !quantity}
            onClick={() =>
              onSave({ ...service, price, quantity, comments, files, amount })
            }
          >
            {t('global:genericButtons.saveChanges')}
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
